import { Component, OnInit, OnDestroy, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  VehicleDataService,
  MotorcycleBrand,
} from '../services/vehicle-data.service';

@Component({
  selector: 'app-motorcycle-brand-selector',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div class="motorcycle-brand-selector">
      <div class="form-group">
        <select
          id="motorcycle-brand"
          [formControl]="brandControl"
          class="form-control"
          (blur)="onBlur()"
          [class.invalid]="shouldShowError()"
        >
          <option [ngValue]="null">Choose your motorcycle brand</option>
          <option *ngFor="let brand of brands" [ngValue]="brand.id">
            {{ brand.name }}
          </option>
        </select>

        <div *ngIf="loadingBrands" class="loading-indicator">
          <div class="spinner"></div>
          Loading brands...
        </div>

        <div *ngIf="!loadingBrands && brands.length === 0" class="no-brands">
          <small class="text-muted">Unable to load motorcycle brands.</small>
        </div>
        <div *ngIf="shouldShowError()" class="error-message">
          Brand is required
        </div>
      </div>
    </div>
  `,
  styles: [`
    .motorcycle-brand-selector {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .form-control {
      padding: 12px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 14px;
      transition: border-color 0.2s;
      background: white;
      width: 100%;
    }

    .form-control:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .form-control:disabled {
      background: #f9fafb;
      color: #9ca3af;
      cursor: not-allowed;
    }

    select.form-control {
      cursor: pointer;
    }

    .form-control.invalid {
      border-color: #ef4444;
    }

    .loading-indicator {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #6b7280;
      font-size: 13px;
      margin-top: 4px;
    }

    .spinner {
      width: 16px;
      height: 16px;
      border: 2px solid #e5e7eb;
      border-top: 2px solid #3b82f6;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .no-brands {
      padding: 8px 12px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      margin-top: 4px;
    }

    .text-muted {
      color: #6b7280;
      font-size: 13px;
    }

    .error-message {
      color: #ef4444;
      font-size: 13px;
      margin-top: 4px;
      padding: 6px 8px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 4px;
    }

    @media (max-width: 767px) {
      .form-control {
        font-size: 16px;
      }
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MotorcycleBrandSelectorComponent),
      multi: true,
    },
  ],
})
export class MotorcycleBrandSelectorComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() required = true;

  brandControl: FormControl;
  brands: MotorcycleBrand[] = [];
  loadingBrands = false;
  private userTouched = false;

  private destroy$ = new Subject<void>();
  private onChange = (value: string) => {};
  private onTouched = () => {};
  private isInternalUpdate = false;

  constructor(
    private fb: FormBuilder,
    private vehicleDataService: VehicleDataService
  ) {
    this.brandControl = new FormControl(
      null,
      this.required ? Validators.required : null
    );
  }

  ngOnInit(): void {
    this.loadBrands();
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBlur(): void {
    this.userTouched = true;
    this.onTouched();
  }

  shouldShowError(): boolean {
    return this.brandControl.invalid && this.userTouched;
  }

  writeValue(value: string | null): void {
    this.userTouched = false;

    if (!value || value.trim() === '') {
      this.clearSelection();
      return;
    }

    if (this.brands.length === 0) {
      setTimeout(() => this.applyValue(value), 100);
      return;
    }

    this.applyValue(value);
  }

  private applyValue(value: string): void {
    const trimmedValue = value.trim();
    const normalizedValue = this.normalizeString(trimmedValue);

    const matchingBrand = this.brands.find(
      (b) => this.normalizeString(b.name) === normalizedValue
    );

    if (matchingBrand) {
      this.isInternalUpdate = true;
      this.brandControl.setValue(matchingBrand.id, { emitEvent: false });
      this.isInternalUpdate = false;
    } else {
      this.clearSelection();
    }
  }

  private normalizeString(str: string): string {
    return str.toLowerCase().trim().replace(/\s+/g, ' ');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.brandControl.disable();
    } else {
      this.brandControl.enable();
    }
  }

  clearSelection(): void {
    this.isInternalUpdate = true;
    this.brandControl.setValue(null);
    this.isInternalUpdate = false;
    this.emitValue();
  }

  private setupFormSubscriptions(): void {
    this.brandControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(100),
        distinctUntilChanged()
      )
      .subscribe(() => {
        if (!this.isInternalUpdate) {
          this.onTouched();
          this.emitValue();
        }
      });
  }

  private loadBrands(): void {
    this.loadingBrands = true;
    this.vehicleDataService
      .getMotorcycleBrands()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (brands) => {
          this.brands = brands;
          this.loadingBrands = false;
        },
        error: (error) => {
          console.error('Error loading motorcycle brands:', error);
          this.loadingBrands = false;
        },
      });
  }

  private emitValue(): void {
    const brandId = this.brandControl.value;
    const brand = this.brands.find((b) => b.id === brandId);
    this.onChange(brand?.name || '');
  }
}
